<template>
	<div v-if="booking">
		<BContainer>
			<template v-if="booking">
				<BOverlay id="cancelled-overlay" :show="isCancelled && !overlayDismissed"
					rounded="sm" class="p-2">
					<template #overlay>
						<div style="backdrop-filter: blur(3px);">
							<h3>
								This booking has been <span class="text-danger">cancelled</span>. Would you like to restart it?
							</h3>
							<div class="mt-4 d-flex justify-content-center gap-sm">
								<BButton variant="white"
									@click="dismissOverlay">
									No, don't restart
								</BButton>
								<BButton variant="dark" class="d-flex align-items-center"
									@click="$bvModal.show('booking-restart-modal')">
									<span class="mr-2">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="1.5em"
											height="1.5em"
											fill="currentColor"
											viewBox="0 0 16 16">
											<path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41m-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z" />
											<path fill-rule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5 5 0 0 0 8 3M3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9z" />
										</svg>
									</span>
									Restart Booking
								</BButton>
							</div>
						</div>
					</template>
					<div class="d-flex justify-content-between flex-column flex-md-row gap-y-sm">
						<h3 class="mb-0">
							{{ booking.frequency }} {{ booking.service }} <span class="text-muted">clean</span>
						</h3>
						<div class="d-flex gap-sm">
							<BButton v-if="!isCancelled" variant="white"
								size="sm"
								class="d-flex align-items-center" @click="$bvModal.show('booking-update-modal')">
								<BIcon icon="pencil-fill"
									font-scale="0.75"
									class="mr-2"></BIcon>
								Change Details
							</BButton>
							<BButton v-if="!isCancelled" variant="white"
								size="sm"
								class="d-flex align-items-center" @click="$bvModal.show('booking-update-frequency-modal')">
								<BIcon icon="pencil-fill"
									font-scale="0.75"
									class="mr-2"></BIcon>
								Change Frequency
							</BButton>
							<template v-if="isCancelled">
								<BButton variant="dark" size="sm"
									class="d-flex align-items-center" @click="$bvModal.show('booking-restart-modal')">
									<span class="mr-2">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="1.5em"
											height="1.5em"
											fill="currentColor"
											viewBox="0 0 16 16">
											<path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41m-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z" />
											<path fill-rule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5 5 0 0 0 8 3M3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9z" />
										</svg>
									</span>
									Restart
								</BButton>
							</template>
							<template v-else-if="!booking.pending_cancellation">
								<BButton variant="white" size="sm"
									class="d-flex align-items-center" @click="$bvModal.show('cancel-booking-modal')">
									<BIcon icon="x"
										font-scale="1"
										class="mr-2 text-danger"></BIcon>
									Cancel
								</BButton>
							</template>
						</div>
					</div>
					<div class="mt-2 mt-md-0 mb-2 mb-lg-2">
						<BBadge :variant="statusBadgeVariant" :class="{
							'border': booking.status !== 'Accepted' || booking.status !== 'Confirmed',
						}">
							{{ booking.status }}
						</BBadge>
						<BBadge v-if="booking.pending_cancellation" variant="badge badge-warning"
							class="ml-1">
							Cancellation Requested
						</BBadge>
					</div>
					<div>
						<BIcon icon="geo-alt-fill"></BIcon>
						{{ booking.address }}, {{ booking.suburb.name }}
					</div>
					<div class="my-2"></div>
					<div>
						{{ booking.days }} {{ pluralize('day', booking.days) }}
						{{ booking.hours }} {{ pluralize('hour', booking.hours) }}
					</div>
					<div class="my-2"></div>
					<BRow style="row-gap: 1rem;">
						<BCol v-if="services.length > 0" lg="6">
							<BCard body-class="p-0">
								<div class="px-4 pt-4">
									<h3>Upcoming Services</h3>
								</div>
								<BListGroup flush class="mb-2">
									<BListGroupItem v-for="(service, serviceIndex) in services" :key="service.id">
										<div class="d-flex gap-sm">
											<div>
												{{ formatDate(service.date, { format: 'dddd MMMM DD' }) }}
											</div>
											<div v-if="serviceIndex === 0" class="text-muted">
												<BIcon icon="arrow-left-short"></BIcon>
												Next Service
											</div>
										</div>
									</BListGroupItem>
								</BListGroup>
							</BCard>
						</BCol>
						<BCol v-if="booking.payment_method === 'Credit Card' || booking.can_setup_credit_card_payment" lg="6">
							<BCard :class="{
								'primary': booking.can_setup_credit_card_payment,
							}">
								<template v-if="booking.payment_method === 'Credit Card'">
									<div class="d-flex justify-content-between align-items-center">
										<h3 class="mb-0">
											Payment Method
										</h3>
										<BButton size="sm" variant="white"
											class="d-flex align-items-center" @click="$bvModal.show('credit-card-modal')">
											<BIcon icon="pencil-fill"
												font-scale="0.75"
												class="mr-2"></BIcon>
											Change
										</BButton>
									</div>
									<div class="my-4"></div>
									<template v-if="booking.stripe_card_id">
										<CreditCard :brand="booking.stripe_card_brand" :last-four="booking.stripe_card_last4" />
									</template>
									<template v-else-if="user.stripe_card_id">
										<CreditCard :brand="user.stripe_card_brand" :last-four="user.stripe_card_last4" />
									</template>
								</template>
								<template v-else-if="booking.can_setup_credit_card_payment">
									<h3>
										Setup Credit Card Payments
									</h3>
									<div class="mb-4">
										Currently you're setup to pay with Cash on the day of your clean. If you'd like to pay with a credit card, you can setup your card details by clicking below.
									</div>
									<BButton variant="primary" :disabled="isCancelled"
										@click="$bvModal.show('credit-card-modal')">
										Setup Credit Card Payments
									</BButton>
								</template>
							</BCard>
						</BCol>
						<BCol lg="6">
							<BCard>
								<div class="d-flex justify-content-between align-items-center">
									<h3>
										Your Cleaner
									</h3>
									<template v-if="booking.cleaner">
										<a :href="`sms:${booking.cleaner.phone}`"
											class="btn btn-sm btn-dark rounded-lg d-flex align-items-center" :disabled="isCancelled">
											<BIcon icon="chat-fill" class="mr-2 text-success"></BIcon>
											Message
										</a>
									</template>
								</div>
								<template v-if="!booking.cleaner">
									We're still looking for a cleaner. We'll let you know when a cleaner has been assigned to your booking.
								</template>
								<template v-else>
									<div>
										{{ booking.cleaner.first_name }} {{ booking.cleaner.last_name }}
									</div>
									<a :href="`tel:${booking.cleaner.phone}`">
										{{ booking.cleaner.phone }}
									</a>
								</template>
							</BCard>
						</BCol>
					</BRow>
				</BOverlay>
			</template>
		</BContainer>
		<credit-card-modal id="credit-card-modal"
			@card-updated="cardUpdated" />
		<booking-update-modal id="booking-update-modal" :booking="booking"
			@booking-updated="fetchBooking" />
		<booking-update-frequency-modal id="booking-update-frequency-modal" :booking="booking"
			@booking-updated="fetchBooking" />
		<booking-cancel-modal id="cancel-booking-modal" :booking="booking"
			@booking-canceled="fetchBooking" />
		<booking-restart-modal id="booking-restart-modal" :booking="booking" />
	</div>
</template>

<script>
	import dayjs from 'dayjs';
	import { pluralize, formatDate } from '@js/utilities/index.js';
	import CreditCardModal from './CreditCardModal.vue';
	import BookingUpdateModal from './UpdateModal.vue';
	import BookingUpdateFrequencyModal from './UpdateFrequencyModal.vue';
	import BookingCancelModal from './CancelModal.vue';
	import BookingRestartModal from './RestartModal.vue';
	import CreditCard from '@js/components/my/ui/CreditCard.vue';

	export default {
		components: {
			CreditCardModal,
			BookingUpdateModal,
			BookingUpdateFrequencyModal,
			BookingCancelModal,
			BookingRestartModal,
			CreditCard,
		},
		props: {
			uniqueId: {
				type: String,
				required: true,
			}
		},
		data() {
			return {
				booking: null,
				overlayDismissed: false,
				services: [],
			}
		},
		computed: {
			isCancelled() {
				return this.booking.status === 'Cancelled';
			},
			statusBadgeVariant() {
				switch (this.booking.status) {
					case 'Cancelled':
						return 'badge badge-danger';
					case 'Accepted':
						return 'primary';
					case 'Confirmed':
						return 'primary';
					case 'Completed':
						return 'badge badge-success';
					case 'Paused':
						return 'light';
					default:
						return 'light';
				}
			}
		},
		created() {
			this.fetchBooking();
			this.fetchServices();
		},
		methods: {
			pluralize,
			formatDate,
			fetchBooking() {
				this.$axios.get(`/api/bookings/${this.uniqueId}`).then(response => {
					this.booking = response.data.data;
				})
			},
			fetchServices() {
				this.$axios.get(`/api/bookings/${this.uniqueId}/services`, {
					params: {
						from_date: dayjs().format('YYYY-MM-DD'),
						per_page: 5,
						orderBy: 'date',
						order: 'asc',
					}
				}).then(response => {
					this.services = response.data.data;
				})
			},
			cardUpdated() {
				this.fetchBooking();
				this.$store.dispatch('auth/fetchUser');
			},
			dismissOverlay() {
				this.overlayDismissed = true;
			}
		}
	}
</script>

<style lang="scss" scoped>
  @import '../../../../sass/_variables.scss';

  .list-group.list-group-flush {
    .list-group-item {
      padding-inline: 1.5rem;
      padding-block: 0.5rem;
    }
  }
</style>
